import { useCallback, useEffect, useMemo, useState, type PropsWithChildren } from 'react'
import {
  GoogleAuthProvider,
  PhoneAuthProvider,
  RecaptchaVerifier,
  onAuthStateChanged,
  signInWithCredential,
  signInWithPhoneNumber,
  signInWithPopup,
  signOut,
  type User,
} from 'firebase/auth'
import { Capacitor, type PluginListenerHandle } from '@capacitor/core'
import { FirebaseAuthentication } from '@capacitor-firebase/authentication'
import { collection, doc, getDoc, getDocs, onSnapshot, serverTimestamp, setDoc, type Unsubscribe } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { AuthContext, type AuthContextValue, type AuthUser } from './authContextCore'
import { isExplicitInviteReviewRoute, resolvePendingLinkInvite } from './authPendingLinkInvite'
import { firebaseAuth, firebaseFunctions, firestoreDb } from '../services/firebase'
import { type UserLedger } from '../services/ledgerService'
import type { UserProfile } from '../types/schema'
import { recordConsent } from '../services/consentService'
import { flushMemory } from '../services/memoryService'
import { clearAllPersistedState } from '../hooks/usePersistedState'
import { runDraftGuards } from '../utils/draftGuardRegistry'
import {
  DEFAULT_ACCOUNT_ROLE,
  DEFAULT_DISPLAY_PREFERENCE,
  computeDisplayName,
  isPhoneOnlyIdentity,
  normalizeAccountRole,
  splitName,
} from '../utils/userIdentity'
import {
  claimPendingInvite,
  clearPendingLinkToken,
  consumePendingLinkToken,
  fetchInviteByToken,
  peekPendingLinkToken,
} from '../services/inviteService'
import { consumePendingRedirect } from '../services/pendingRedirect'
import { recordHelperDeviceBinding } from '../services/helper/helperDeviceBinding'
import { registerMobilePushToken } from '../services/mobilePushRegistrationService'
import { getWrapperRole } from '../utils/wrapperRole'
import { testingOverrideAllowsAllFeatures } from '../config/testingOverrides'
import { bindHelperOutboxUser, clearHelperOutboxForUser } from '../services/helper/helperOutboxDb'
import { clearWorkspaceStorage } from './workspaceContextCore'
import { clearMemberOutboxForUser } from '../services/memberOutboxService'
import { runFullLocalWipe, verifyUserTokenRevocation } from '../services/authRevocation'
import { isSharedDevicePath } from '../utils/sharedDeviceRoute'
import { useNls } from '../nls/nlsProvider'
import { ControlNotice, Button } from '../components/Common'
import { restoreAccountDeletion } from '../services/accountDeletionService'
import { formatPurgeDate } from '../utils/purgeDate'

const promoteHelperClaim = httpsCallable<
  { mobileNumber?: string | null; phoneNumber?: string | null },
  { ok: boolean; alreadyPromoted: boolean }
>(firebaseFunctions, 'promoteHelperClaim')
const helperFinalizationByUid = new Map<string, Promise<void>>()

const DEFAULT_USER_PROFILE_ECONOMY = {
  credits: 250,
  hasSetFirstGoal: false,
  dailyCratesClaimed: false,
  dailyTasksCompletedCount: 0,
  referralCount: 0,
  referredBy: null,
} satisfies Omit<UserProfile, 'uid' | 'email'>

const toAuthUser = (user: User): AuthUser => ({
  uid: user.uid,
  email: user.email,
  displayName: (user as { displayName: string | null }).displayName,
  photoURL: user.photoURL,
  phoneNumber: user.phoneNumber,
  emailVerified: user.emailVerified,
  isAnonymous: user.isAnonymous,
  providerId: user.providerData[0]?.providerId ?? null,
  getIdTokenResult: (force?: boolean) => user.getIdTokenResult(force),
})

const clampDailyTasksCompletedCount = (value: unknown) => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return 0
  return Math.min(2, Math.max(0, Math.round(value)))
}

const clampReferralCount = (value: unknown) => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return 0
  return Math.min(10, Math.max(0, Math.round(value)))
}

const normalizeUserProfile = (
  uid: string,
  email: string | null,
  data: Partial<UserProfile> | undefined,
): UserProfile => {
  const displayName = typeof data?.displayName === 'string' ? data.displayName : null
  const { firstName, lastName } = splitName(displayName)
  const normalized: UserProfile = {
    uid,
    email: typeof data?.email === 'string' && data.email ? data.email : email ?? '',
    role: normalizeAccountRole(data?.role),
    firstName: typeof data?.firstName === 'string' ? data.firstName : firstName || null,
    lastName: typeof data?.lastName === 'string' ? data.lastName : lastName || null,
    username: typeof data?.username === 'string' ? data.username : null,
    phone: typeof data?.phone === 'string' ? data.phone : null,
    address: typeof data?.address === 'string' ? data.address : null,
    educationNotes: typeof data?.educationNotes === 'string' ? data.educationNotes : null,
    displayName,
    photoURL: typeof data?.photoURL === 'string' ? data.photoURL : null,
    profileImage: data?.profileImage && typeof data.profileImage === 'object'
      ? {
          source: data.profileImage.source === 'GOOGLE' || data.profileImage.source === 'AVATAR' ? data.profileImage.source : null,
          avatarId: typeof data.profileImage.avatarId === 'string' ? data.profileImage.avatarId : null,
          updatedAt: data.profileImage.updatedAt,
        }
      : null,
    displayPreferences: {
      type: data?.displayPreferences?.type === 'USERNAME' || data?.displayPreferences?.type === 'PHONE' || data?.displayPreferences?.type === 'FIRST_LAST'
        ? data.displayPreferences.type
        : DEFAULT_DISPLAY_PREFERENCE,
      computedName: typeof data?.displayPreferences?.computedName === 'string'
        ? data.displayPreferences.computedName
        : '',
    },
    helperMetadata: data?.helperMetadata && typeof data.helperMetadata === 'object'
      ? {
          internalUid: typeof data.helperMetadata.internalUid === 'string' ? data.helperMetadata.internalUid : uid,
          mobileNumber: typeof data.helperMetadata.mobileNumber === 'string' ? data.helperMetadata.mobileNumber : '',
        }
      : null,
    metadata: {
      ...(data?.metadata && typeof data.metadata === 'object' ? data.metadata : {}),
      onboardingCompleted: true,
    },
    credits: typeof data?.credits === 'number' && Number.isFinite(data.credits)
      ? Math.max(0, Math.round(data.credits))
      : DEFAULT_USER_PROFILE_ECONOMY.credits,
    hasSetFirstGoal: typeof data?.hasSetFirstGoal === 'boolean'
      ? data.hasSetFirstGoal
      : DEFAULT_USER_PROFILE_ECONOMY.hasSetFirstGoal,
    dailyCratesClaimed: typeof data?.dailyCratesClaimed === 'boolean'
      ? data.dailyCratesClaimed
      : DEFAULT_USER_PROFILE_ECONOMY.dailyCratesClaimed,
    dailyTasksCompletedCount: clampDailyTasksCompletedCount(data?.dailyTasksCompletedCount),
    referralCount: clampReferralCount(data?.referralCount),
    referredBy: typeof data?.referredBy === 'string' && data.referredBy ? data.referredBy : null,
    deletion: data?.deletion,
    consent: data?.consent && typeof data.consent === 'object'
      ? {
          termsVersion: String(data.consent.termsVersion ?? ''),
          privacyVersion: String(data.consent.privacyVersion ?? ''),
          acceptedAt: data.consent.acceptedAt,
        }
      : undefined,
  }
  normalized.displayPreferences = {
    ...normalized.displayPreferences,
    computedName: normalized.displayPreferences?.computedName || computeDisplayName(normalized),
  }
  return normalized
}

const getMissingUserProfileDefaults = (profile: UserProfile, data: Partial<UserProfile> | undefined) => {
  const missingDefaults: Partial<UserProfile> = {}

  if (typeof data?.credits !== 'number') missingDefaults.credits = profile.credits
  if (typeof data?.hasSetFirstGoal !== 'boolean') missingDefaults.hasSetFirstGoal = profile.hasSetFirstGoal
  if (typeof data?.dailyCratesClaimed !== 'boolean') missingDefaults.dailyCratesClaimed = profile.dailyCratesClaimed
  if (typeof data?.dailyTasksCompletedCount !== 'number') {
    missingDefaults.dailyTasksCompletedCount = profile.dailyTasksCompletedCount
  }
  if (typeof data?.referralCount !== 'number') missingDefaults.referralCount = profile.referralCount
  if (!('referredBy' in (data ?? {}))) missingDefaults.referredBy = profile.referredBy
  if (data?.role !== 'MEMBER' && data?.role !== 'HELPER') missingDefaults.role = DEFAULT_ACCOUNT_ROLE
  if (!data?.displayPreferences?.type) {
    missingDefaults.displayPreferences = {
      type: DEFAULT_DISPLAY_PREFERENCE,
      computedName: computeDisplayName(profile),
    }
  }
  if (!data?.metadata || data.metadata.onboardingCompleted !== true) {
    missingDefaults.metadata = {
      ...(data?.metadata && typeof data.metadata === 'object' ? data.metadata : {}),
      onboardingCompleted: true,
    }
  }

  return missingDefaults
}

const ensureUserDocument = async (user: User): Promise<UserProfile> => {
  const userRef = doc(firestoreDb, 'users', user.uid)
  const snapshot = await getDoc(userRef)
  const existingData = snapshot.exists() ? snapshot.data() as Partial<UserProfile> : undefined
  const normalizedProfile = normalizeUserProfile(user.uid, user.email, existingData)
  const missingEconomyDefaults = getMissingUserProfileDefaults(normalizedProfile, existingData)
  const hasMissingEconomyDefaults = Object.keys(missingEconomyDefaults).length > 0

  await setDoc(userRef, {
    email: user.email,
    displayName: (user as { displayName: string | null }).displayName,
    photoURL: existingData?.profileImage?.source === 'AVATAR' ? normalizedProfile.photoURL : user.photoURL,
    uid: user.uid,
    role: normalizedProfile.role,
    firstName: normalizedProfile.firstName,
    lastName: normalizedProfile.lastName,
    displayPreferences: normalizedProfile.displayPreferences,
    metadata: normalizedProfile.metadata,
    ...(snapshot.exists() ? missingEconomyDefaults : DEFAULT_USER_PROFILE_ECONOMY),
    ...(snapshot.exists() ? { updatedAt: serverTimestamp() } : { createdAt: serverTimestamp() }),
  }, { merge: true })

  if (!snapshot.exists()) {
    console.info('[AuthContext] Initialized virtual economy profile defaults for new user.', {
      uid: user.uid,
      credits: DEFAULT_USER_PROFILE_ECONOMY.credits,
    })
  } else if (hasMissingEconomyDefaults) {
    console.info('[AuthContext] Backfilled missing virtual economy profile defaults for existing user.', {
      uid: user.uid,
      fields: Object.keys(missingEconomyDefaults),
    })
  }

  // On first sign-in with no consent field: record consent once.
  if (!existingData?.consent) {
    try {
      await recordConsent(user.uid)
    } catch (err) {
      console.warn('[AuthContext] Could not record initial consent on sign-in', err)
    }
  }

  return normalizedProfile
}

const HELPER_SESSION_HARD_TIMEOUT_MS = 6_000
// Absolute upper bound on how long the boot loader can wait for auth state to
// settle. Every downstream setter is best-effort; this guarantees the shell
// eventually renders even if a promise never resolves.
const AUTH_HARD_TIMEOUT_MS = 10_000

/**
 * Runs the parts of the helper post-login flow that MUST land before we call
 * the session "ready": the custom-claim promotion and a fresh ID token. Every
 * other side-effect (invite sweep, device binding, push registration) is
 * deferred so a stalled native call — most infamously the push-permission
 * prompt on Android's first launch, which never resolves if the user backgrounds
 * the app — can't hold the boot splash open forever.
 */
const finalizeHelperPhoneSessionCritical = async (signedInUser: User) => {
  const phoneE164 = signedInUser.phoneNumber ?? null
  await promoteHelperClaim({ phoneNumber: phoneE164, mobileNumber: phoneE164 })
  await signedInUser.getIdToken(true)
}

const finalizeHelperPhoneSessionBackground = async (signedInUser: User) => {
  const phoneE164 = signedInUser.phoneNumber ?? null
  const displayName = (signedInUser as { displayName: string | null }).displayName ?? phoneE164

  const claimingUser = {
    uid: signedInUser.uid,
    email: signedInUser.email,
    phoneE164,
    displayName,
  }

  if (!isExplicitInviteReviewRoute()) {
    const linkToken = consumePendingLinkToken()
    if (linkToken) {
      try {
        const invite = await fetchInviteByToken(linkToken)
        if (invite && invite.status === 'PENDING') {
          await claimPendingInvite(invite.id, claimingUser)
        }
      } catch (error) {
        console.warn('[helper-login] link-token claim failed', error)
      }
    }
  }

  try { await signedInUser.getIdToken(true) } catch (error) {
    console.warn('[helper-login] id token refresh failed', error)
  }
  try { await recordHelperDeviceBinding(signedInUser.uid) } catch (error) {
    console.warn('[helper-login] device binding failed', error)
  }
  try { await registerMobilePushToken(signedInUser.uid, 'HELPER', { force: true }) } catch (error) {
    // Never throws in practice — its own catch swallows — but belt-and-braces
    // this so a future refactor can't reintroduce the boot-hang symptom.
    console.warn('[helper-login] push registration failed', error)
  }
}

const waitForHelperSessionIfNeeded = async (signedInUser: User) => {
  if (getWrapperRole() !== 'helper') return
  const existing = helperFinalizationByUid.get(signedInUser.uid)
  if (existing) {
    await existing
    return
  }
  const criticalRace = new Promise<void>((resolve) => {
    const timeout = window.setTimeout(() => {
      console.warn('[helper-login] critical finalization timed out; unblocking boot.')
      resolve()
    }, HELPER_SESSION_HARD_TIMEOUT_MS)
    finalizeHelperPhoneSessionCritical(signedInUser)
      .catch((error) => console.warn('[helper-login] helper session finalization failed', error))
      .finally(() => {
        window.clearTimeout(timeout)
        resolve()
      })
  })
  helperFinalizationByUid.set(signedInUser.uid, criticalRace)
  try {
    await criticalRace
  } finally {
    // Fire and forget — never blocks boot. Cleanup after the background work
    // completes so a second sign-in on the same session can skip past it.
    void finalizeHelperPhoneSessionBackground(signedInUser).finally(() => {
      helperFinalizationByUid.delete(signedInUser.uid)
    })
  }
}

const SESSION_USER_STORAGE_KEY = 'timelining-session-user'
const SESSION_USER_TTL_MS = 7 * 24 * 60 * 60 * 1000

type CachedSessionUser = {
  user: AuthUser
  cachedAt: number
}

const readCachedSessionUser = (): AuthUser | null => {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.sessionStorage.getItem(SESSION_USER_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as CachedSessionUser | null
    if (!parsed || typeof parsed.cachedAt !== 'number') return null
    if (Date.now() - parsed.cachedAt > SESSION_USER_TTL_MS) {
      window.sessionStorage.removeItem(SESSION_USER_STORAGE_KEY)
      return null
    }
    return parsed.user
  } catch {
    return null
  }
}

const writeCachedSessionUser = (user: AuthUser) => {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.setItem(
      SESSION_USER_STORAGE_KEY,
      JSON.stringify({ user, cachedAt: Date.now() } satisfies CachedSessionUser),
    )
  } catch {
    // sessionStorage unavailable (private mode / quota) — fall back to in-memory only
  }
}

const clearCachedSessionUser = () => {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.removeItem(SESSION_USER_STORAGE_KEY)
  } catch {
    // ignore
  }
}

const clearWorkspaceCache = () => {
  window.localStorage.removeItem('activeWorkspaceId')
  window.localStorage.removeItem('appMode')
  window.localStorage.removeItem('timelining-active-workspace')
  window.localStorage.removeItem('timelining-app-mode')
  Object.keys(window.localStorage)
    .filter((key) => key.toLowerCase().includes('workspace'))
    .forEach((key) => window.localStorage.removeItem(key))
}

export const AuthProvider = ({ children }: PropsWithChildren) => {
  const { t } = useNls()
  const [user, setUser] = useState<AuthUser | null>(() => {
    const cached = readCachedSessionUser()
    if (cached?.uid) {
      void bindHelperOutboxUser(cached.uid)
    }
    return cached
  })
  const [isAuthLoading, setIsAuthLoading] = useState(true)
  const [wasLedgerDormant, setWasLedgerDormant] = useState(false)
  const [accountTier, setAccountTier] = useState('FREE')
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null)
  const [isSessionLocked, setIsSessionLocked] = useState(false)
  const [sessionNotice, setSessionNotice] = useState<string | null>(null)

  const lockSession = useCallback(() => setIsSessionLocked(true), [])
  const unlockSession = useCallback(() => setIsSessionLocked(false), [])

  // Post-login redirect: when the user has just signed in AND the intended
  // path (stashed by main.tsx while they were signed-out) is safe and
  // different from where we currently are, push history there. Runs whenever
  // `user` flips to a non-null value; the localStorage slot is drained in the
  // consume() call so the redirect fires exactly once per sign-in.
  useEffect(() => {
    if (!user) return
    const target = consumePendingRedirect()
    if (!target) return
    if (typeof window === 'undefined') return
    const currentUrl = `${window.location.pathname}${window.location.search}`
    if (currentUrl === target) return
    window.history.pushState(null, '', target)
    window.dispatchEvent(new PopStateEvent('popstate'))
  }, [user])

  useEffect(() => {
    let ledgerUnsubscribe: Unsubscribe | null = null
    let profileUnsubscribe: Unsubscribe | null = null
    // Safety net for cold-launch on native wrappers: the ledger onSnapshot is
    // supposed to flip isAuthLoading false on its very first emission, but on
    // first-install cold starts we've seen the whole chain stall until the app
    // is restarted (native push permission prompt, Firestore warm-up, IndexedDB
    // init). Guarantee the loader lifts after AUTH_HARD_TIMEOUT_MS no matter
    // which downstream promise wedges.
    let authTimeoutId: number | null = null
    const armAuthTimeout = () => {
      if (authTimeoutId !== null) return
      authTimeoutId = window.setTimeout(() => {
        console.warn('[AuthContext] auth-loading safety timeout — forcing isAuthLoading=false')
        setIsAuthLoading(false)
        authTimeoutId = null
      }, AUTH_HARD_TIMEOUT_MS)
    }
    const clearAuthTimeout = () => {
      if (authTimeoutId === null) return
      window.clearTimeout(authTimeoutId)
      authTimeoutId = null
    }
    const releaseAuthLoading = () => {
      clearAuthTimeout()
      setIsAuthLoading(false)
    }

    const unsubscribe = onAuthStateChanged(firebaseAuth, (nextUser) => {
      ledgerUnsubscribe?.()
      ledgerUnsubscribe = null
      profileUnsubscribe?.()
      profileUnsubscribe = null
      armAuthTimeout()

      if (!nextUser) {
        const cachedUser = readCachedSessionUser()
        if (cachedUser) {
          // Firebase dropped the session, but the sessionStorage credential is still within its
          // TTL. Keep the user signed in locally so the landing page does not
          // redirect — only fall through to a hard logout once the cache expires.
          setUser(cachedUser)
          setIsAuthLoading(false)
          return
        }
        clearAllPersistedState()
        clearWorkspaceStorage()
        void bindHelperOutboxUser(null)
        setUser(null)
        setUserProfile(null)
        setWasLedgerDormant(false)
        setAccountTier('FREE')
        releaseAuthLoading()
        return
      }

      const nextAuthUser = toAuthUser(nextUser)
      writeCachedSessionUser(nextAuthUser)
      void bindHelperOutboxUser(nextUser.uid)
      setUser(nextAuthUser)
      const helperSessionReady = waitForHelperSessionIfNeeded(nextUser)
      void ensureUserDocument(nextUser)
        .then((profile) => {
          setUserProfile(profile)
          profileUnsubscribe = onSnapshot(
            doc(firestoreDb, 'users', nextUser.uid),
            (snapshot) => {
              setUserProfile(normalizeUserProfile(nextUser.uid, nextUser.email, snapshot.exists() ? snapshot.data() as Partial<UserProfile> : undefined))
            },
            (error) => {
              console.warn('Could not read user profile snapshot.', error)
            },
          )
        })
        .catch((error) => {
          console.warn('Could not update user profile document.', error)
          setUserProfile(normalizeUserProfile(nextUser.uid, nextUser.email, undefined))
        })

      // Deep-link intent: if a link token was stashed before sign-in, claim it now.
      if (peekPendingLinkToken()) {
        void resolvePendingLinkInvite(nextUser).catch((error) => {
          console.warn('[AuthContext] Link token claim threw — ignored.', error)
        })
      }

      if (isPhoneOnlyIdentity(nextUser)) {
        setWasLedgerDormant(false)
        setAccountTier('FREE')
        void helperSessionReady.finally(releaseAuthLoading)
      } else {
        try {
          ledgerUnsubscribe = onSnapshot(
            doc(firestoreDb, 'users', nextUser.uid, 'wallet', 'personal'),
            (snapshot) => {
              if (!snapshot.exists()) {
                setWasLedgerDormant(false)
                setAccountTier('FREE')
                void helperSessionReady.finally(releaseAuthLoading)
                return
              }

              const data = snapshot.data() as Partial<UserLedger> & {
                plan?: string
                tier?: string
                subscriptionTier?: string
                accountTier?: string
              }
              const wasDormant = data.status === 'DORMANT'
              const nextTier = data.accountTier ?? data.subscriptionTier ?? data.tier ?? data.plan ?? 'FREE'

              if (wasDormant) {
                clearWorkspaceCache()
                void setDoc(snapshot.ref, { status: 'ACTIVE' }, { merge: true }).catch((error) => {
                  console.warn('Could not reactivate dormant ledger.', error)
                })
              }

              setWasLedgerDormant(wasDormant)
              setAccountTier(nextTier)
              void helperSessionReady.finally(releaseAuthLoading)
            },
            (error) => {
              console.warn('Could not read user ledger snapshot.', error)
              setWasLedgerDormant(false)
              setAccountTier('FREE')
              void helperSessionReady.finally(releaseAuthLoading)
            },
          )
        } catch (error) {
          console.warn('Could not attach user ledger listener.', error)
          setWasLedgerDormant(false)
          setAccountTier('FREE')
          void helperSessionReady.finally(releaseAuthLoading)
        }
      }
    })

    return () => {
      ledgerUnsubscribe?.()
      profileUnsubscribe?.()
      clearAuthTimeout()
      unsubscribe()
    }
  }, [])

  const signInWithGoogle = useCallback(async () => {
    // signInWithPopup fails inside Capacitor's WebView — Google rejects it as
    // an "insecure browser". On native we invoke the platform's own Google
    // Sign-In via @capacitor-firebase/authentication, then bridge the returned
    // ID token back into the JS SDK so Firestore, offline persistence, and the
    // rest of AuthContext keep working through firebaseAuth.currentUser.
    if (Capacitor.isNativePlatform()) {
      const result = await FirebaseAuthentication.signInWithGoogle()
      const idToken = result.credential?.idToken
      if (!idToken) {
        throw new Error('Google sign-in returned no idToken.')
      }
      const credential = GoogleAuthProvider.credential(idToken)
      await signInWithCredential(firebaseAuth, credential)
      return
    }
    const provider = new GoogleAuthProvider()
    provider.setCustomParameters({ prompt: 'select_account' })
    await signInWithPopup(firebaseAuth, provider)
  }, [])

  const sendPhoneVerification = useCallback(async (phoneNumber: string) => {
    if (!Capacitor.isNativePlatform()) {
      if (!testingOverrideAllowsAllFeatures()) {
        throw new Error('Phone sign-in is only supported inside the helper app.')
      }
      let container: HTMLElement | undefined
      if (typeof document !== 'undefined') {
        const containerId = 'recaptcha-verifier-container'
        let el = document.getElementById(containerId)
        if (!el) {
          el = document.createElement('div')
          el.id = containerId
          document.body.appendChild(el)
        }
        container = el
      }
      const win = typeof window !== 'undefined' ? (window as unknown as { __helperRecaptchaVerifier?: RecaptchaVerifier }) : null
      if (win?.__helperRecaptchaVerifier) {
        try {
          win.__helperRecaptchaVerifier.clear()
        } catch {
          // ignore
        }
      }
      const verifier = new RecaptchaVerifier(firebaseAuth, container ?? 'recaptcha-verifier-container', {
        size: 'invisible',
      })
      if (win) win.__helperRecaptchaVerifier = verifier
      try {
        const confirmationResult = await signInWithPhoneNumber(firebaseAuth, phoneNumber, verifier)
        return confirmationResult.verificationId
      } catch (error) {
        try {
          verifier.clear()
        } catch {
          // ignore
        }
        if (win) delete win.__helperRecaptchaVerifier
        throw error
      }
    }
    return new Promise<string>((resolve, reject) => {
      let settled = false
      let codeSentHandle: PluginListenerHandle | null = null
      let failedHandle: PluginListenerHandle | null = null
      let timeoutId: ReturnType<typeof setTimeout> | null = null

      const cleanup = () => {
        if (timeoutId) clearTimeout(timeoutId)
        void codeSentHandle?.remove()
        void failedHandle?.remove()
      }

      const finish = (callback: () => void) => {
        if (settled) return
        settled = true
        cleanup()
        callback()
      }

      void (async () => {
        try {
          codeSentHandle = await FirebaseAuthentication.addListener('phoneCodeSent', (event) => {
            if (!event.verificationId) {
              finish(() => reject(new Error('Phone sign-in did not return a verificationId.')))
              return
            }
            finish(() => resolve(event.verificationId))
          })

          failedHandle = await FirebaseAuthentication.addListener('phoneVerificationFailed', (event) => {
            finish(() => reject(new Error(event.message || 'Phone verification failed.')))
          })

          timeoutId = setTimeout(() => {
            finish(() => reject(new Error('Timed out waiting for the SMS verification code.')))
          }, 70000)

          await FirebaseAuthentication.signInWithPhoneNumber({ phoneNumber, timeout: 60 })
        } catch (error) {
          finish(() => reject(error))
        }
      })()
    })
  }, [])

  const confirmPhoneCode = useCallback(async (verificationId: string, code: string) => {
    const credential = PhoneAuthProvider.credential(verificationId, code)
    const userCredential = await signInWithCredential(firebaseAuth, credential)
    if (getWrapperRole() === 'helper') {
      await waitForHelperSessionIfNeeded(userCredential.user)
    }
  }, [])

  const logout = useCallback(async (options?: {
    keepSession?: boolean
    revocationMessage?: string
    skipDraftGuards?: boolean
  }) => {
    // Give any registered guards (e.g. unsaved Manage drafts) a chance to
    // prompt the user before tearing the session down, unless skipped for revocation.
    if (!options?.skipDraftGuards) {
      const proceed = await runDraftGuards()
      if (!proceed) return
    }
    if (user && !user.isAnonymous) await flushMemory(user.uid)
    if (!options?.keepSession) {
      clearCachedSessionUser()
    }
    clearPendingLinkToken()
    clearAllPersistedState()
    clearWorkspaceStorage()
    setIsSessionLocked(false)
    if (user?.uid) {
      await clearHelperOutboxForUser(user.uid).catch(() => {})
      await clearMemberOutboxForUser(user.uid).catch(() => {})
    }
    void bindHelperOutboxUser(null)

    // Full local wipe across client tiers
    await runFullLocalWipe(user?.uid)

    if (options?.revocationMessage) {
      setSessionNotice(options.revocationMessage)
    }

    await signOut(firebaseAuth)
  }, [user])

  // R39 A12.a: On app start while online, and on every offline -> online change, force a token refresh.
  // Revocation honoured on reconnect; shared-device routes keep their own revoke path.
  useEffect(() => {
    if (!user) return undefined
    const pathname = typeof window !== 'undefined' ? window.location.pathname : ''
    if (isSharedDevicePath(pathname)) return undefined

    const checkRevocation = async () => {
      await verifyUserTokenRevocation(firebaseAuth.currentUser, {
        onRevoked: async () => {
          await logout({
            skipDraftGuards: true,
            revocationMessage: t('NLSSessionRevokedMessage'),
          })
        },
      })
    }

    void checkRevocation()

    const handleOnline = () => {
      void checkRevocation()
    }
    window.addEventListener('online', handleOnline)
    return () => {
      window.removeEventListener('online', handleOnline)
    }
  }, [user, logout, t])

  const getInitialWorkspace = useCallback(async () => {
    if (!user) return null
    try {
      const snapshot = await getDocs(collection(firestoreDb, 'users', user.uid, 'workspaceMemberships'))
      const activeMemberships = snapshot.docs
        .map((entry) => ({ workspaceId: entry.id, role: String(entry.data().role ?? 'VIEWER'), status: String(entry.data().status ?? 'ACTIVE') }))
        .filter((entry) => entry.status === 'ACTIVE')
      const isHelperOnly = activeMemberships.length > 0 && activeMemberships.every((entry) => entry.role === 'HELPER')
      const targetWorkspace = isHelperOnly
        ? activeMemberships.find((entry) => entry.role === 'HELPER')
        : activeMemberships.find((entry) => entry.role !== 'HELPER')
      return targetWorkspace ?? activeMemberships[0] ?? null
    } catch (error) {
      console.warn('[AuthContext] Could not read initial workspace mirror.', error)
      return null
    }
  }, [user])

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      currentUser: user,
      isAuthLoading,
      wasLedgerDormant,
      accountTier,
      userProfile,
      signInWithGoogle,
      sendPhoneVerification,
      confirmPhoneCode,
      signOutUser: logout,
      logout,
      getInitialWorkspace,
      isSessionLocked,
      lockSession,
      unlockSession,
      sessionNotice,
    }),
    [accountTier, confirmPhoneCode, getInitialWorkspace, isAuthLoading, isSessionLocked, lockSession, logout, sendPhoneVerification, sessionNotice, signInWithGoogle, unlockSession, user, userProfile, wasLedgerDormant],
  )

  const [isRestoringAccount, setIsRestoringAccount] = useState(false)
  const [restoreError, setRestoreError] = useState<string | null>(null)

  const handleRestoreAccount = useCallback(async () => {
    setIsRestoringAccount(true)
    setRestoreError(null)
    try {
      await restoreAccountDeletion()
    } catch (err) {
      setRestoreError(err instanceof Error ? err.message : 'Could not restore account.')
    } finally {
      setIsRestoringAccount(false)
    }
  }, [])

  if (userProfile?.deletion?.status === 'PENDING') {
    return (
      <AuthContext.Provider value={value}>
        <AccountDeletionGraceScreen
          userProfile={userProfile}
          onRestore={handleRestoreAccount}
          isRestoring={isRestoringAccount}
          restoreError={restoreError}
          onLogout={logout}
        />
      </AuthContext.Provider>
    )
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export interface AccountDeletionGraceScreenProps {
  userProfile?: { deletion?: { status?: string; purgeAfter?: unknown } } | null
  onRestore: () => Promise<void> | void
  isRestoring?: boolean
  restoreError?: string | null
  onLogout: () => Promise<void> | void
}

export const AccountDeletionGraceScreen = ({
  userProfile,
  onRestore,
  isRestoring = false,
  restoreError = null,
  onLogout,
}: AccountDeletionGraceScreenProps) => {
  const { t } = useNls()
  if (userProfile?.deletion?.status !== 'PENDING') return null
  const purgeDate = formatPurgeDate(userProfile.deletion.purgeAfter)
  return (
    <div
      className="accountDeletionGraceScreen"
      role="alert"
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
      }}
    >
      <div style={{ maxWidth: '480px', width: '100%', textAlign: 'center' }}>
        <ControlNotice
          tone="warning"
          title={t('NLSAccountDeletionGraceTitle')}
          message={t('NLSAccountDeletionGraceNotice', { date: purgeDate })}
        />
        {restoreError ? <p className="authError" style={{ marginTop: '12px' }}>{restoreError}</p> : null}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', marginTop: '24px' }}>
          <Button
            variant="primary"
            onClick={() => void onRestore()}
            loading={isRestoring}
          >
            {t('NLSRestoreAccountAction')}
          </Button>
          <Button
            variant="secondary"
            onClick={() => void onLogout()}
          >
            {t('NLSCommonActionSignOut')}
          </Button>
        </div>
      </div>
    </div>
  )
}

